import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Sparkles, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Group invitation landing page — `/groups/invite/:token`
 *
 * The recipient lands here from the email link. We look up the invite,
 * pre-fill their details, and require them to set a password + tick a
 * consent checkbox before we create their account and attach them to the
 * group. After accept they're auto-logged-in and bounced to the group view.
 */
export default function GroupInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    password: "",
    consent: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/groups/invite/${token}`);
        if (cancelled) return;
        setInvite(r.data);
        setForm((f) => ({
          ...f,
          name: r.data.member_name || "",
          phone: r.data.member_phone || "",
        }));
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err?.response?.data?.detail || "This invitation is invalid or expired."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="group-invite-loading">
        <div className="overline">Loading invitation…</div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div
        className="max-w-md mx-auto px-6 py-20 text-center"
        data-testid="group-invite-not-found"
      >
        <h1 className="font-display text-3xl text-[var(--emerald)]">
          Invitation not found.
        </h1>
        <p className="text-sm text-[var(--ink-2)] mt-3">
          This invitation link is no longer valid. Please ask the group owner
          to resend it.
        </p>
        <button
          onClick={() => navigate("/")}
          className="btn-primary mt-6"
          data-testid="group-invite-home-btn"
        >
          Back to Sasha
        </button>
      </div>
    );
  }

  if (invite.already_accepted) {
    return (
      <div
        className="max-w-md mx-auto px-6 py-20 text-center"
        data-testid="group-invite-already-accepted"
      >
        <h1 className="font-display text-3xl text-[var(--emerald)]">
          You've already accepted this invitation.
        </h1>
        <p className="text-sm text-[var(--ink-2)] mt-3">
          Sign in with the email <strong>{invite.member_email}</strong> to view
          the group's trip details.
        </p>
        <button
          onClick={() => navigate("/login")}
          className="btn-primary mt-6"
          data-testid="group-invite-signin-btn"
        >
          Sign in
        </button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the consent statement to continue.");
      return;
    }
    if ((form.password || "").length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/groups/invite/${token}/accept`, {
        password: form.password,
        consent: form.consent,
        name: form.name,
        phone: form.phone,
      });
      localStorage.setItem("sasha_token", r.data.token);
      await refresh();
      toast.success("Welcome to the trip.");
      navigate(`/groups/${r.data.group_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't accept the invitation.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-6 py-12" data-testid="group-invite-page">
      <div className="overline flex items-center gap-2">
        <Sparkles size={14} strokeWidth={1.5} className="text-[var(--gold)]" />
        Janté Travel Concierges
      </div>
      <h1 className="font-display text-4xl text-[var(--emerald)] mt-2 leading-tight">
        {invite.owner_name} invited you to a Janté trip.
      </h1>
      <p className="text-base text-[var(--ink-2)] mt-4 leading-relaxed">
        You're being added to <strong>{invite.group_name}</strong>
        {invite.group_destination ? ` — ${invite.group_destination}` : ""}.
        Set up your account below to view the itinerary and stay informed as
        the trip is planned.
      </p>

      <form onSubmit={submit} className="lux-card !p-8 mt-8" data-testid="group-invite-form">
        <div className="space-y-5">
          <div>
            <label className="overline">Email</label>
            <input
              type="email"
              value={invite.member_email}
              disabled
              className="form-input mt-2 w-full bg-[var(--ivory-2)] cursor-not-allowed"
              data-testid="group-invite-email-input"
            />
          </div>
          <div>
            <label className="overline">Your name *</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="form-input mt-2 w-full"
              data-testid="group-invite-name-input"
            />
          </div>
          <div>
            <label className="overline">Phone (optional)</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="form-input mt-2 w-full"
              data-testid="group-invite-phone-input"
            />
          </div>
          <div>
            <label className="overline">Create a password *</label>
            <input
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="At least 6 characters"
              className="form-input mt-2 w-full"
              data-testid="group-invite-password-input"
            />
          </div>
        </div>

        <label className="mt-6 flex items-start gap-3 text-sm text-[var(--ink)] cursor-pointer">
          <input
            type="checkbox"
            required
            checked={form.consent}
            onChange={(e) => setForm({ ...form, consent: e.target.checked })}
            className="mt-1 flex-shrink-0"
            data-testid="group-invite-consent-checkbox"
          />
          <span className="leading-relaxed">
            <ShieldCheck
              size={14}
              strokeWidth={1.5}
              className="inline text-[var(--gold)] mr-1"
            />
            I consent to Janté Travel Concierges storing my contact information
            and contacting me about this trip and future Janté offerings.
          </span>
        </label>

        <button
          type="submit"
          disabled={submitting || !form.consent}
          className="btn-primary mt-7 w-full disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="group-invite-submit-btn"
        >
          {submitting ? "Joining the trip…" : "Accept & view the trip"}
          {!submitting && <ArrowRight size={14} strokeWidth={1.5} />}
        </button>
      </form>
    </div>
  );
}
